import { FiltersDropdown } from '@shared/components/ui/FiltersDropdown';
import { SimpleFilterContent, type SimpleFilterField } from '@shared/components/ui/SimpleFilter';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useClientsDropdown } from '../../hooks';
import { PayoutFilters } from '../../models';
import { ExportButton } from './ExportButton';

interface FiltersSectionProps {
  filters: PayoutFilters;
  onFiltersChange: (filters: Partial<PayoutFilters>) => void;
  onResetFilters: () => void;
  exportColumns?: string[];
}

export const FiltersSection = ({ filters, onFiltersChange, onResetFilters, exportColumns }: FiltersSectionProps) => {
  const { t } = useTranslation('codPayouts');
  const { clients, isLoading: isLoadingClients } = useClientsDropdown();

  // 🎯 Configuración de campos para SimpleFilter
  const fields: SimpleFilterField[] = useMemo(
    () => [
      {
        key: 'client_id',
        label: t('filters.client', 'Cliente'),
        type: 'select',
        options: clients.map((client) => ({
          label: client.name,
          value: String(client.id),
        })),
        allOption: {
          label: t('filters.all_clients', 'Todos los clientes'),
          value: 'all',
        },
        isLoadingOptions: isLoadingClients,
        placeholder: isLoadingClients
          ? t('filters.loading_clients', 'Cargando...')
          : t('filters.select_client', 'Seleccionar cliente'),
        minWidth: '180px',
        maxWidth: '220px',
      },
      {
        key: 'date_from',
        label: t('filters.date_from', 'Fecha desde'),
        type: 'date',
        dateConfig: {
          placeholder: t('filters.select_date', 'Seleccionar fecha'),
        },
        minWidth: '200px',
      },
      {
        key: 'date_to',
        label: t('filters.date_to', 'Fecha hasta'),
        type: 'date',
        dateConfig: {
          placeholder: t('filters.select_date', 'Seleccionar fecha'),
        },
        minWidth: '200px',
      },
    ],
    [t, clients, isLoadingClients],
  );

  // 🎯 Convertir valores de filtros a formato que SimpleFilter espera
  const filterValues = useMemo(() => {
    const values: Record<string, any> = {
      date_from: filters.date_from ? new Date(filters.date_from) : undefined,
      date_to: filters.date_to ? new Date(filters.date_to) : undefined,
      client_id: filters.client_id ? String(filters.client_id) : 'all',
    };
    return values;
  }, [filters]);

  // 🎯 Handler para cambios en los filtros
  const handleFilterChange = (newValues: Partial<Record<string, any>>) => {
    const updatedFilters: Partial<PayoutFilters> = {};

    // Convertir valores de vuelta al formato del modelo
    Object.keys(newValues).forEach((key) => {
      const value = newValues[key];

      if (key === 'client_id') {
        updatedFilters.client_id = value === 'all' || !value ? undefined : parseInt(value, 10);
      } else if (key === 'date_from' || key === 'date_to') {
        // Convertir Date a string formato 'YYYY-MM-DD' (equivalente a Y-m-d del API)
        // El API espera date_from y date_to en formato Y-m-d (YYYY-MM-DD)
        if (value instanceof Date) {
          updatedFilters[key as 'date_from' | 'date_to'] = value.toLocaleDateString('en-CA'); // Produce formato YYYY-MM-DD
        } else if (value) {
          updatedFilters[key as 'date_from' | 'date_to'] = value;
        } else {
          updatedFilters[key as 'date_from' | 'date_to'] = undefined;
        }
      }
    });

    onFiltersChange(updatedFilters);
  };

  const activeFiltersCount = useMemo(
    () =>
      fields.filter((field) => {
        const value = filterValues[field.key];
        if (value === undefined || value === null || value === '') return false;
        if (field.type === 'select' && field.allOption && value === field.allOption.value) return false;
        return true;
      }).length,
    [fields, filterValues],
  );

  const filterTitle = t('filters.title', 'Filtros');

  return (
    <div className="mb-4 flex items-center gap-3 flex-wrap">
      <FiltersDropdown
        title={filterTitle}
        activeFiltersCount={activeFiltersCount}
        triggerTestId="filters-toggle-button"
        dropdownContentClassName="sm:min-w-[500px] sm:max-w-[500px] p-4 bg-body-bg text-foreground border border-card-border"
      >
        <SimpleFilterContent
          fields={fields}
          values={filterValues}
          onChange={handleFilterChange}
          onReset={onResetFilters}
          showClearButton={true}
          clearButtonText={t('filters.clear', 'Limpiar')}
          translations={{
            searching: t('filters.searching', 'Buscando...'),
            selectDate: t('filters.select_date', 'Seleccionar fecha'),
            clear: t('filters.clear', 'Limpiar'),
          }}
        />
      </FiltersDropdown>
      {exportColumns && <ExportButton filters={filters} columns={exportColumns} />}
    </div>
  );
};
